import { apiClient } from './client';
import type { ProfitAllocation } from './allocationEndpoints';

export interface ProfitEntry {
  id: string;
  gross_amount_cents: number;
  gross_amount_dollars: number;
  notes?: string;
  distributed: boolean;
  entry_date: string;
  entered_by_user_id: string;
  entered_by?: {
    id: string;
    email: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

export interface ProfitEntryListResponse {
  profit_entries: ProfitEntry[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  page_totals: {
    gross_amount_cents: number;
  };
}

export interface CreateProfitEntryParams {
  gross_amount_cents: number;
  notes?: string;
  entry_date?: string;
}

export interface CreateProfitEntryResponse {
  id: string;
  created_at: string;
  profit_entry: ProfitEntry;
}

export interface EntryAllocationsResponse {
  profit_entry: {
    id: string;
    entry_date: string;
    end_date: string;
    gross_profit_cents: number;
    distributed: boolean;
    notes: string | null;
    entered_by: {
      name: string;
    };
  };
  allocations: ProfitAllocation[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  totals: {
    gross_share_cents: number;
    company_cut_cents: number;
    net_share_cents: number;
  };
}

const profitEntryEndpoints = {
  list: async (params?: { 
    from?: string; 
    to?: string; 
    distributed?: 'all' | 'yes' | 'no';
    min_amount?: number;
    max_amount?: number;
    page?: number; 
    per?: number;
  }): Promise<ProfitEntryListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.distributed) queryParams.append('distributed', params.distributed);
    if (params?.min_amount !== undefined) queryParams.append('min_amount', params.min_amount.toString());
    if (params?.max_amount !== undefined) queryParams.append('max_amount', params.max_amount.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per) queryParams.append('per', params.per.toString());
    
    return await apiClient.get(`/api/admin/profits?${queryParams.toString()}`);
  },

  create: async (data: CreateProfitEntryParams): Promise<CreateProfitEntryResponse> => {
    return await apiClient.post('/api/admin/profits', { profit_entry: data });
  },

  void: async (id: string, reason?: string): Promise<{ message: string; adjusting_entry: ProfitEntry }> => {
    return await apiClient.post(`/api/admin/profits/${id}/void`, { reason });
  },

  allocate: async (id: string): Promise<{ message: string; allocations: ProfitAllocation[] }> => {
    return await apiClient.post(`/api/admin/profits/${id}/allocate`);
  },

  getEntryAllocations: async (
    id: string,
    filters?: { page?: number; per?: number }
  ): Promise<EntryAllocationsResponse> => {
    const queryParams = new URLSearchParams();
    
    if (filters?.page) {
      queryParams.append('page', filters.page.toString());
    }
    if (filters?.per) {
      queryParams.append('per', filters.per.toString());
    }
    
    const query = queryParams.toString();
    return await apiClient.get(`/api/admin/profits/${id}/allocations${query ? '?' + query : ''}`);
  }
};

export default profitEntryEndpoints;
